import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, Search, User, Bot, Clock, AlertTriangle, Shield, ShieldOff, Eye, Play, Pause, ChevronRight, X, Phone } from 'lucide-react';

const STATUS_COLORS = {
  HUMAN_ACTIVE: 'bg-red-500 text-white',
  BOT_FREE: 'bg-emerald-500 text-white',
  TTL_EXPIRED: 'bg-zinc-500 text-white',
};

const CLASSIFICATION_COLORS = {
  BOT_ALLOWED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  BOT_CONFIRMED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  BOT_MESSAGE_TRACKED: 'bg-blue-100 text-blue-800 border-blue-200',
  WOULD_BLOCK_BOT: 'bg-amber-100 text-amber-800 border-amber-200',
  BOT_BLOCKED: 'bg-red-100 text-red-800 border-red-200',
  HUMAN_CONFIRMED_ANDROID: 'bg-orange-100 text-orange-800 border-orange-200',
  HUMAN_DETECTED_BY_EXCLUSION: 'bg-orange-100 text-orange-800 border-orange-200',
  UNSURE_OUTGOING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  MANUAL_RELEASE: 'bg-teal-100 text-teal-800 border-teal-200',
  MANUAL_PAUSE: 'bg-purple-100 text-purple-800 border-purple-200',
  SESSION_UPDATED: 'bg-orange-100 text-orange-800 border-orange-200',
};

function formatTTL(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function SummaryCards({ summary }) {
  if (!summary) return null;
  const { config, events_24h } = summary;

  const modeLabel = !config.enabled && config.shadow_mode ? 'SHADOW MODE' :
    config.enabled && !config.shadow_mode ? 'BLOQUEIO REAL' :
    config.enabled && config.shadow_mode ? 'SHADOW (com flag)' : 'DESATIVADO';

  const modeColor = !config.enabled && config.shadow_mode ? 'bg-amber-500' :
    config.enabled && !config.shadow_mode ? 'bg-red-600' :
    config.enabled && config.shadow_mode ? 'bg-amber-500' : 'bg-zinc-500';

  const cards = [
    { label: 'Humano Ativo', value: summary.human_active_now, icon: User, color: 'text-red-500', bg: 'bg-red-50' },
    { label: 'Bot Livre', value: summary.bot_free, icon: Bot, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'Would Block 24h', value: events_24h.WOULD_BLOCK_BOT, icon: Eye, color: 'text-amber-500', bg: 'bg-amber-50' },
    { label: 'Bloqueios 24h', value: events_24h.BOT_BLOCKED, icon: ShieldOff, color: 'text-red-500', bg: 'bg-red-50' },
    { label: 'Incertos 24h', value: events_24h.UNSURE_OUTGOING, icon: AlertTriangle, color: 'text-yellow-500', bg: 'bg-yellow-50' },
    { label: 'Humano Android 24h', value: events_24h.HUMAN_CONFIRMED_ANDROID, icon: Phone, color: 'text-orange-500', bg: 'bg-orange-50' },
  ];

  return (
    <div data-testid="summary-section">
      <div className="flex items-center gap-3 mb-4">
        <span data-testid="mode-badge" className={`${modeColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>{modeLabel}</span>
        <span className="text-xs text-zinc-500">TTL: {config.ttl_minutes}min</span>
        <span className="text-xs text-zinc-500">Contatos: {summary.total_contacts_monitored}</span>
        <span className="text-xs text-zinc-500">Bot msgs: {summary.total_bot_messages_tracked}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((c, i) => (
          <Card key={i} className={`${c.bg} border-0`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <c.icon className={`w-4 h-4 ${c.color}`} />
                <span className="text-xs text-zinc-600">{c.label}</span>
              </div>
              <p className={`text-2xl font-bold mt-1 ${c.color}`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function ContactsTable({ contacts, onSelect, onRelease, onPause }) {
  if (!contacts || contacts.length === 0) {
    return <div className="text-center py-8 text-zinc-400">Nenhum contato monitorado ainda</div>;
  }

  return (
    <div className="overflow-x-auto" data-testid="contacts-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 text-left text-xs text-zinc-500 uppercase">
            <th className="py-2 px-3">Contato</th>
            <th className="py-2 px-3">Status</th>
            <th className="py-2 px-3">TTL Restante</th>
            <th className="py-2 px-3">Humano</th>
            <th className="py-2 px-3">Bot</th>
            <th className="py-2 px-3">Evento</th>
            <th className="py-2 px-3 text-center">Pausas</th>
            <th className="py-2 px-3 text-center">Risco</th>
            <th className="py-2 px-3"></th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((c) => (
            <tr key={c.contact_jid} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors cursor-pointer" onClick={() => onSelect(c.contact_jid)}>
              <td className="py-2.5 px-3">
                <div className="font-medium text-zinc-800">{c.phone_formatted}</div>
                <div className="text-xs text-zinc-400">{c.contact_jid}</div>
              </td>
              <td className="py-2.5 px-3">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[c.status_atual] || 'bg-zinc-200'}`}>
                  {c.status_atual === 'HUMAN_ACTIVE' ? 'HUMANO' : c.status_atual === 'BOT_FREE' ? 'BOT' : 'EXPIRADO'}
                </span>
              </td>
              <td className="py-2.5 px-3">
                {c.status_atual === 'HUMAN_ACTIVE' ? (
                  <span className="font-mono text-red-600 font-medium">{formatTTL(c.ttl_remaining_seconds)}</span>
                ) : '—'}
              </td>
              <td className="py-2.5 px-3 text-xs text-zinc-500">{formatTime(c.last_human_message_at)}</td>
              <td className="py-2.5 px-3 text-xs text-zinc-500">{formatTime(c.last_bot_message_at)}</td>
              <td className="py-2.5 px-3">
                {c.last_classification ? (
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${CLASSIFICATION_COLORS[c.last_classification] || 'bg-zinc-100'}`}>
                    {c.last_classification}
                  </span>
                ) : '—'}
              </td>
              <td className="py-2.5 px-3 text-center">
                <span className={`font-medium ${c.pause_count > 3 ? 'text-red-500' : c.pause_count > 0 ? 'text-amber-500' : 'text-zinc-400'}`}>
                  {c.pause_count}
                </span>
              </td>
              <td className="py-2.5 px-3 text-center">
                {c.has_unsure_recent && <AlertTriangle className="w-4 h-4 text-yellow-500 inline" />}
              </td>
              <td className="py-2.5 px-3">
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  {c.status_atual === 'HUMAN_ACTIVE' ? (
                    <Button data-testid={`release-btn-${c.contact_jid}`} variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:text-emerald-700" onClick={() => onRelease(c.contact_jid)}>
                      <Play className="w-3 h-3 mr-1" /> Liberar
                    </Button>
                  ) : (
                    <Button data-testid={`pause-btn-${c.contact_jid}`} variant="ghost" size="sm" className="h-7 text-xs text-amber-600 hover:text-amber-700" onClick={() => onPause(c.contact_jid)}>
                      <Pause className="w-3 h-3 mr-1" /> Pausar
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContactDetail({ contactJid, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contactJid) return;
    setLoading(true);
    api.get(`/whatsapp/human-monitor/contacts/${contactJid}`)
      .then(res => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [contactJid]);

  if (!contactJid) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="contact-detail-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 p-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-lg">Detalhes do Contato</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-400">Carregando...</div>
        ) : !detail || detail.error ? (
          <div className="p-8 text-center text-red-400">Contato nao encontrado</div>
        ) : (
          <div className="p-4 space-y-4">
            {detail.session && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Sessao</CardTitle></CardHeader>
                <CardContent className="text-xs space-y-1">
                  <div><strong>JID:</strong> {detail.session.contact_jid}</div>
                  <div><strong>Status:</strong> <span className={`px-1.5 py-0.5 rounded ${STATUS_COLORS[detail.session.status_atual]}`}>{detail.session.status_atual}</span></div>
                  <div><strong>Humano ativo ate:</strong> {formatDateTime(detail.session.human_active_until)}</div>
                  <div><strong>Ultimo humano:</strong> {formatDateTime(detail.session.last_human_message_at)}</div>
                  <div><strong>Ultimo bot:</strong> {formatDateTime(detail.session.last_bot_message_at)}</div>
                  <div><strong>Pausas:</strong> {detail.session.pause_count}</div>
                  <div><strong>Lock version:</strong> {detail.session.lock_version}</div>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Timeline de Eventos ({detail.events?.length || 0})</CardTitle></CardHeader>
              <CardContent>
                {detail.events?.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {detail.events.map((ev, i) => (
                      <div key={i} className={`p-2 rounded text-xs border ${CLASSIFICATION_COLORS[ev.classification] || 'bg-zinc-50 border-zinc-200'}`}>
                        <div className="flex justify-between items-start">
                          <span className="font-medium">{ev.classification || ev.event_type}</span>
                          <span className="text-zinc-400">{formatDateTime(ev.created_at)}</span>
                        </div>
                        <div className="text-zinc-500 mt-0.5">
                          {ev.event_type} | {ev.action_taken} {ev.source && `| source=${ev.source}`} {ev.message_id && `| msg=${ev.message_id.slice(0,12)}...`}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-zinc-400 text-xs">Nenhum evento registrado</div>}
              </CardContent>
            </Card>
            {detail.bot_messages?.length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Mensagens do Bot ({detail.bot_messages.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detail.bot_messages.map((bm, i) => (
                      <div key={i} className="text-xs flex justify-between text-zinc-500">
                        <span className="font-mono">{bm.message_id?.slice(0, 20)}...</span>
                        <span>{formatDateTime(bm.sent_at)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function WhatsAppMonitorPage() {
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [page, setPage] = useState(1);
  const [selectedJid, setSelectedJid] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, conRes] = await Promise.all([
        api.get('/whatsapp/human-monitor/summary'),
        api.get('/whatsapp/human-monitor/contacts', {
          params: { status: statusFilter !== 'all' ? statusFilter : undefined, search: search || undefined, sort_by: sortBy, page, page_size: 50 }
        })
      ]);
      setSummary(sumRes.data);
      setContacts(conRes.data.contacts || []);
      setTotal(conRes.data.total || 0);
      setLastUpdate(new Date());
    } catch (e) {
      console.error('Monitor fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, sortBy, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleRelease = async (jid) => {
    if (!window.confirm(`Liberar robo para ${jid}?`)) return;
    try {
      await api.post(`/whatsapp/human-monitor/contacts/${jid}/release`);
      fetchData();
    } catch (e) {
      alert('Erro ao liberar: ' + e.message);
    }
  };

  const handlePause = async (jid) => {
    if (!window.confirm(`Pausar robo para ${jid}? O TTL sera aplicado.`)) return;
    try {
      await api.post(`/whatsapp/human-monitor/contacts/${jid}/pause`);
      fetchData();
    } catch (e) {
      alert('Erro ao pausar: ' + e.message);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-4 md:p-6" data-testid="whatsapp-monitor-page">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Monitor WhatsApp</h1>
            <p className="text-xs text-zinc-500">Handoff Humano x Robo - Tempo Real</p>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdate && <span className="text-xs text-zinc-400">{formatTime(lastUpdate.toISOString())}</span>}
            <Button data-testid="auto-refresh-toggle" variant={autoRefresh ? 'default' : 'outline'} size="sm" className="h-8 text-xs"
              onClick={() => setAutoRefresh(!autoRefresh)}>
              <RefreshCw className={`w-3 h-3 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto 15s' : 'Parado'}
            </Button>
            <Button data-testid="refresh-btn" variant="outline" size="sm" className="h-8" onClick={fetchData}>
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <SummaryCards summary={summary} />

        {/* Filters */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
                <Input data-testid="search-input" className="pl-8 h-8 text-sm" placeholder="Buscar telefone..." value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
              </div>
              {['all', 'human_active', 'bot_free', 'unsure'].map((s) => (
                <Button key={s} data-testid={`filter-${s}`} variant={statusFilter === s ? 'default' : 'outline'} size="sm" className="h-8 text-xs"
                  onClick={() => { setStatusFilter(s); setPage(1); }}>
                  {s === 'all' ? 'Todos' : s === 'human_active' ? 'Humano Ativo' : s === 'bot_free' ? 'Bot Livre' : 'Incertos'}
                </Button>
              ))}
              <select data-testid="sort-select" className="h-8 text-xs border rounded px-2 bg-white" value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}>
                <option value="human_active_first">Humano primeiro</option>
                <option value="ttl_expiring">TTL expirando</option>
                <option value="pause_count">Mais pausas</option>
                <option value="recent">Mais recente</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-zinc-400">Carregando...</div>
            ) : (
              <>
                <ContactsTable contacts={contacts} onSelect={setSelectedJid} onRelease={handleRelease} onPause={handlePause} />
                {total > 50 && (
                  <div className="flex justify-center gap-2 p-3 border-t">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                    <span className="text-xs text-zinc-500 self-center">Pagina {page}</span>
                    <Button variant="outline" size="sm" disabled={contacts.length < 50} onClick={() => setPage(p => p + 1)}>Proxima</Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Drawer */}
      <ContactDetail contactJid={selectedJid} onClose={() => setSelectedJid(null)} />
    </div>
  );
}
